import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { client, urlFor } from "../sanity/client";

const DIGITAL_QUERY = `*[_type == "digital" && !(_id in path("drafts.**"))] | order(_createdAt desc) {
  _id, name, slug, category, image, price, description, inStock, featured
}`;

const categories = [
  { value: "all", label: "All Services" },
  { value: "asset-management", label: "Asset Management" },
  { value: "charger-management", label: "Charger Management" },
  { value: "electricity-billing", label: "Electricity Billing" },
  { value: "energy-management", label: "Energy Management" },
];

const categoryColors = {
  "asset-management":     { color: "#1e4d8c", bg: "#eaf0fb", border: "#bccfe9" },
  "charger-management":   { color: "#0e6680", bg: "#e6f5f9", border: "#aad4e3" },
  "electricity-billing":  { color: "#8a5a0f", bg: "#faf3e5", border: "#e6cfa0" },
  "energy-management":    { color: "#2d6a4f", bg: "#eaf4ef", border: "#c2ddd0" },
};

function DigitalCard({ item }) {
  const [hovered, setHovered] = useState(false);
  const theme = categoryColors[item.category] || categoryColors["asset-management"];

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "white",
        borderRadius: "16px",
        border: `1.5px solid ${hovered ? theme.color + "40" : "#ebebeb"}`,
        overflow: "hidden",
        transition: "all 0.22s ease",
        transform: hovered ? "translateY(-4px)" : "translateY(0)",
        boxShadow: hovered ? `0 16px 40px ${theme.color}15` : "0 2px 8px rgba(0,0,0,0.04)",
        cursor: "pointer",
      }}
    >
      <div style={{
        height: "200px", background: theme.bg,
        display: "flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden", position: "relative",
      }}>
        {item.image ? (
          <img
            src={urlFor(item.image).width(300).height(200).fit("crop").url()}
            alt={item.name}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <div style={{ fontSize: "48px", opacity: 0.3 }}>💡</div>
        )}
        <div style={{
          position: "absolute", top: "12px", left: "12px",
          background: "white", color: theme.color,
          fontSize: "10px", fontWeight: "800",
          padding: "4px 10px", borderRadius: "6px",
          fontFamily: "'Sora', sans-serif",
          letterSpacing: "0.5px", textTransform: "uppercase",
          border: `1px solid ${theme.border}`,
        }}>
          {categories.find(c => c.value === item.category)?.label || item.category}
        </div>
        {item.inStock && (
          <div style={{
            position: "absolute", top: "12px", right: "12px",
            background: "#f0fdf4", color: "#16a34a",
            fontSize: "10px", fontWeight: "700",
            padding: "4px 10px", borderRadius: "6px",
            fontFamily: "'Sora', sans-serif", border: "1px solid #bbf7d0",
          }}>
            Available ✓
          </div>
        )}
      </div>

      <div style={{ padding: "18px 20px" }}>
        <h3 style={{
          fontFamily: "'Sora', sans-serif", fontSize: "15px", fontWeight: "800",
          color: "#1a1a2e", margin: "0 0 8px", lineHeight: "1.3",
        }}>
          {item.name}
        </h3>
        {item.description && (
          <p style={{
            fontSize: "13px", color: "#6b7280", fontFamily: "'DM Sans', sans-serif",
            lineHeight: "1.6", margin: "0 0 14px",
            display: "-webkit-box", WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical", overflow: "hidden",
          }}>
            {item.description}
          </p>
        )}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "14px" }}>
          {item.price ? (
            <div style={{ fontFamily: "'Sora', sans-serif", fontSize: "17px", fontWeight: "900", color: theme.color }}>
              ₹{item.price.toLocaleString('en-IN')}
            </div>
          ) : (
            <div style={{ fontSize: "13px", color: "#9ca3af", fontFamily: "'DM Sans', sans-serif", fontWeight: "600" }}>
              Price on request
            </div>
          )}
          <button
            style={{
              background: theme.bg, color: theme.color,
              border: `1.5px solid ${theme.border}`,
              borderRadius: "8px", padding: "7px 16px",
              fontSize: "12px", fontWeight: "700",
              cursor: "pointer", fontFamily: "'Sora', sans-serif",
              transition: "all 0.18s ease",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = theme.color; e.currentTarget.style.color = "white"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = theme.bg; e.currentTarget.style.color = theme.color; }}
          >
            Get Quote
          </button>
        </div>
      </div>
    </div>
  );
}

function Digital() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const urlCategory = searchParams.get("category") || "all";
  const activeCategory = categories.find(c => c.value === urlCategory) ? urlCategory : "all";

  const setActiveCategory = (val) => {
    setSearchParams(val === "all" ? {} : { category: val });
  };

  useEffect(() => {
    client.fetch(DIGITAL_QUERY)
      .then((data) => { setItems(data); setLoading(false); })
      .catch((err) => { console.error("Sanity fetch error:", err); setLoading(false); });

    const subscription = client.listen(DIGITAL_QUERY).subscribe(() => {
      client.fetch(DIGITAL_QUERY).then((data) => setItems(data));
    });

    return () => subscription.unsubscribe();
  }, []);

  const filtered = items.filter((p) => {
    const matchCategory = activeCategory === "all" || p.category === activeCategory;
    const matchSearch = p.name?.toLowerCase().includes(search.toLowerCase());
    return matchCategory && matchSearch;
  });

  return (
    <main style={{ background: "#f8fafc", minHeight: "100vh" }}>

      {/* PAGE HEADER */}
      <div style={{ background: "white", borderBottom: "1px solid #e8eaed", padding: "40px 56px 32px" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <p style={{
            fontSize: "11px", fontWeight: "800", color: "#aab0b8",
            letterSpacing: "2px", textTransform: "uppercase",
            fontFamily: "'Sora', sans-serif", marginBottom: "10px",
          }}>
            DIGITAL SERVICES
          </p>
          <h1 style={{
            fontFamily: "'Sora', sans-serif",
            fontSize: "clamp(24px, 3vw, 36px)", fontWeight: "900",
            color: "#1a1a2e", letterSpacing: "-0.8px", margin: "0 0 8px",
          }}>
            Browse All Digital Services
          </h1>
          <p style={{ color: "#6b7280", fontSize: "15px", fontFamily: "'DM Sans', sans-serif" }}>
            {items.length} services from verified Indian vendors
          </p>
        </div>
      </div>

      <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "32px 56px" }}>

        {/* SEARCH — full width like Physical */}
        <div style={{ position: "relative", marginBottom: "16px" }}>
          <input
            type="text"
            placeholder="Search digital services..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: "100%", padding: "11px 44px 11px 16px",
              borderRadius: "10px", border: "1.5px solid #e5e7eb",
              fontSize: "14px", fontFamily: "'DM Sans', sans-serif",
              background: "white", outline: "none",
              transition: "border-color 0.2s", boxSizing: "border-box",
            }}
            onFocus={(e) => e.target.style.borderColor = "#0891b2"}
            onBlur={(e) => e.target.style.borderColor = "#e5e7eb"}
          />
          <span style={{
            position: "absolute", right: "14px", top: "50%",
            transform: "translateY(-50%)", fontSize: "16px",
          }}>🔍</span>
        </div>

        {/* FILTER BUTTONS — below search like Physical */}
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "32px" }}>
          {categories.map((cat) => (
            <button key={cat.value}
              onClick={() => setActiveCategory(cat.value)}
              style={{
                padding: "8px 16px", borderRadius: "8px",
                border: `1.5px solid ${activeCategory === cat.value ? "#0891b2" : "#e5e7eb"}`,
                background: activeCategory === cat.value ? "#0891b2" : "white",
                color: activeCategory === cat.value ? "white" : "#374151",
                fontSize: "12px", fontWeight: "700",
                cursor: "pointer", fontFamily: "'Sora', sans-serif",
                transition: "all 0.18s ease", whiteSpace: "nowrap",
              }}>
              {cat.label}
            </button>
          ))}
        </div>

        {/* LOADING */}
        {loading && (
          <div style={{ textAlign: "center", padding: "80px 0" }}>
            <div style={{
              width: "40px", height: "40px",
              border: "3px solid #e5e7eb", borderTop: "3px solid #0891b2",
              borderRadius: "50%", animation: "spin 0.8s linear infinite",
              margin: "0 auto 16px",
            }} />
            <p style={{ color: "#9ca3af", fontFamily: "'DM Sans', sans-serif" }}>Loading services...</p>
          </div>
        )}

        {/* NO RESULTS */}
        {!loading && filtered.length === 0 && (
          <div style={{ textAlign: "center", padding: "80px 0" }}>
            <div style={{ fontSize: "48px", marginBottom: "16px" }}>💡</div>
            <h3 style={{ fontFamily: "'Sora', sans-serif", fontSize: "20px", fontWeight: "800", color: "#1a1a2e", marginBottom: "8px" }}>
              No services found
            </h3>
            <p style={{ color: "#9ca3af", fontFamily: "'DM Sans', sans-serif" }}>Try a different category or search term</p>
          </div>
        )}

        {/* GRID */}
        {!loading && filtered.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "20px" }}>
            {filtered.map((item) => (
              <DigitalCard key={item._id} item={item} />
            ))}
          </div>
        )}

      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </main>
  );
}

export default Digital;